import type * as React from 'react';
import { cn } from '../lib/cn.ts';

export type EmptyStateProps = {
  readonly title: string;
  readonly description?: React.ReactNode;
  readonly icon?: React.ReactNode;
  readonly action?: React.ReactNode;
  readonly className?: string;
  /**
   * `empty`  — there is genuinely nothing yet.
   * `filtered` — there is data, but this filter matches none of it.
   * `error`  — the request failed.
   *
   * The design (§2.4) requires these three to be visually distinguishable: an
   * "add your first contact" call to action shown after a failed request is a
   * lie about the state of the user's data.
   */
  readonly kind?: 'empty' | 'filtered' | 'error';
};

export function EmptyState({
  title,
  description,
  icon,
  action,
  className,
  kind = 'empty',
}: EmptyStateProps) {
  return (
    <div
      data-kind={kind}
      role={kind === 'error' ? 'alert' : undefined}
      className={cn(
        'flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed',
        'px-6 py-12 text-center',
        kind === 'error' ? 'border-destructive/40 bg-destructive/5' : 'border-border bg-card/40',
        className,
      )}
    >
      {icon === undefined ? null : (
        <div
          className={cn(
            'text-muted-foreground [&_svg]:size-6',
            kind === 'error' && 'text-destructive',
          )}
        >
          {icon}
        </div>
      )}
      <div className="flex flex-col gap-1">
        <p className={cn('text-sm font-medium', kind === 'error' && 'text-destructive')}>{title}</p>
        {description === undefined ? null : (
          <div className="max-w-md text-sm text-muted-foreground">{description}</div>
        )}
      </div>
      {action}
    </div>
  );
}
